import { useEffect, useState } from 'react';
import { useChangeTick } from '../lib/changes';
import { api, hourLabel, slotLabel, type Dashboard as DashboardData, type DashboardCard, type EditOutcome, type OnDuty, type PeriodShift, type Site } from '../lib/api';
import { addDays, dayLabel, quarterOfWeek } from '../lib/dates';
import { navigate } from '../lib/router';
import { AddDialog, EDITABLE_STATUS, ShiftEditor } from './ShiftEdit';

/**
 * The scheduler's home (D31): this week per hospital, what needs attention
 * in the weeks ahead, and what changed recently. Every number is a link to
 * the page where it can be dealt with.
 */
export function Dashboard({ week, sites, editedBy }: { week?: string; sites: Site[]; editedBy: string }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const changes = useChangeTick();
  // Editing from the cards (D93). The week is fetched on the click rather than carried in the
  // dashboard's answer: the editor needs every shift at that hospital that week to find a
  // handover's other half, and the card only knows about one day. Without them the "move
  // handover" option would simply not appear, with nothing to say why.
  const [open, setOpen] = useState<{ site: Site; shift: PeriodShift; siblings: PeriodShift[]; periodStart: string; weekStatus: string } | null>(null);
  const [adding, setAdding] = useState<{ site: Site; date: string; periodStart: string; slot: string } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reload = () => api.dashboard(week).then(setData).catch((e: Error) => setError(e.message));

  /**
   * The dialog announces the change itself, and the tick above reloads on it — so this only says
   * what happened. Reloading here as well would fetch the dashboard twice for one edit.
   */
  const changed = (outcome: EditOutcome, what: string) => {
    setNote(`${what}${outcome.introduced.length ? ` — ${outcome.introduced.length} error(s) overridden.` : '. No new errors.'}`);
  };

  const editShift = async (siteId: string, row: OnDuty) => {
    const site = sites.find((x) => x.id === siteId);
    if (!site || !editedBy) return navigate(`/admin/sites/${siteId}/periods/${row.periodStart}`);
    setOpening(row.id);
    setNote(null);
    try {
      const period = await api.period(siteId, row.periodStart);
      const shift = period.assignments.find((a) => a.id === row.id);
      if (!shift) throw new Error('that shift is no longer in the week — it may have just been changed');
      setOpen({ site, shift, siblings: period.assignments, periodStart: row.periodStart, weekStatus: period.status });
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(null);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    void reload();
  }, [week]);
  // Any announced change — an edit made here, on another screen, or by Callie (D93).
  useEffect(() => {
    if (changes) void reload();
  }, [changes]);

  if (error) return <p className="error">Couldn't load the dashboard ({error}).</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const a = data.attention;
  // Which quarter this week belongs to, by the platform's Friday rule (D15). This was worked
  // out here from the calendar month of the week's Tuesday, which disagrees at the boundary:
  // the week of Tue 30 March came out as Q1, and John confirmed OSMD counts it as Q2.
  const quarter = quarterOfWeek(data.week);
  const isThisWeek = data.today >= data.week && data.today <= data.weekEnd;

  return (
    <section className="dash">
      <div className="gridtools">
        <div className="quarterpicker">
          <button type="button" onClick={() => navigate(`/admin/dashboard?week=${addDays(data.week, -7)}`)} aria-label="Previous week">‹</button>
          <h1>{isThisWeek ? 'This week' : 'Week of'} · {dayLabel(data.week).replace(/^\w+, /, '')} – {dayLabel(data.weekEnd).replace(/^\w+, /, '')}</h1>
          <button type="button" onClick={() => navigate(`/admin/dashboard?week=${addDays(data.week, 7)}`)} aria-label="Next week">›</button>
        </div>
        {!isThisWeek && <button className="linklike" type="button" onClick={() => navigate('/admin/dashboard')}>back to this week</button>}
        <span className="muted">{isThisWeek ? `today, ${dayLabel(data.today)}` : `showing ${dayLabel(data.focusDay)}`}</span>
      </div>

      {data.nearest && (
        <p className="notice">
          Nothing is scheduled for this week yet.
          {data.nearest.next && <> The next scheduled week starts <button className="linklike" type="button" onClick={() => navigate(`/admin/dashboard?week=${data.nearest!.next}`)}>{dayLabel(data.nearest.next)}</button>.</>}
          {data.nearest.previous && <> The most recent one started <button className="linklike" type="button" onClick={() => navigate(`/admin/dashboard?week=${data.nearest!.previous}`)}>{dayLabel(data.nearest.previous)}</button>.</>}
          {!data.nearest.next && !data.nearest.previous && <> No weeks have been published.</>}
        </p>
      )}
      {note && <p className="notice noprint">{note}</p>}
      <div className="cards">
        {data.cards.map((c) => (
          <SiteCard
            key={c.siteId}
            c={c}
            focusDay={data.focusDay}
            quarter={quarter}
            opening={opening}
            onEdit={editedBy ? (row) => void editShift(c.siteId, row) : undefined}
            onAdd={
              editedBy && c.period && EDITABLE_STATUS.has(c.period.status) && sites.some((x) => x.id === c.siteId)
                ? (slot) => setAdding({ site: sites.find((x) => x.id === c.siteId)!, date: data.focusDay, periodStart: c.period!.periodStart, slot })
                : undefined
            }
          />
        ))}
      </div>

      {open && (
        <ShiftEditor
          site={open.site}
          shift={open.shift}
          siblings={open.siblings}
          editedBy={editedBy}
          periodStart={open.periodStart}
          weekStatus={open.weekStatus}
          onClose={() => setOpen(null)}
          onDone={(o, what) => { setOpen(null); changed(o, what); }}
          onOpenWeek={() => navigate(`/admin/sites/${open.site.id}/periods/${open.periodStart}`)}
        />
      )}
      {adding && (
        <AddDialog
          site={adding.site}
          dates={[adding.date]}
          existingDates={[adding.date]}
          editedBy={editedBy}
          periodStart={adding.periodStart}
          slot={adding.slot}
          onClose={() => setAdding(null)}
          onDone={(o) => { setAdding(null); changed(o, 'Added'); }}
        />
      )}

      <div className="dash-cols">
        <section className="card">
          <h2>Needs attention</h2>
          {a.unpublished.length === 0 && a.openSlots.length === 0 && a.credentialing.length === 0 && a.requestWindows.every((w) => !w.open) && a.awaitingApproval.length === 0 && a.onboarding.length === 0 && (
            <p className="muted">Nothing outstanding in the next eight weeks.</p>
          )}
          {a.requestWindows.filter((w) => w.open).map((w) => (
            <Row key={w.quarter} tone={w.responded < w.physicians ? 'warn' : 'ok'} onClick={() => navigate(`/admin/requests?quarter=${w.quarter}`)}>
              <strong>{quarterLabel(w.quarter)} requests</strong> — {w.responded} of {w.physicians} answered · closes {dayLabel(w.closesOn)}
            </Row>
          ))}
          {a.unpublished.map((u) => (
            <Row key={`${u.siteId}|${u.periodStart}`} tone="warn" onClick={() => navigate(`/admin/sites/${u.siteId}/periods/${u.periodStart}`)}>
              <strong>{u.siteName}</strong> week of {dayLabel(u.periodStart).replace(/^\w+, /, '')} is <em>{u.status}</em>, not published
            </Row>
          ))}
          <More label="open slots" items={groupOpen(a.openSlots)} render={(g) => (
            <Row key={`${g.siteId}|${g.positions}|${g.firstWeek}`} tone="err" onClick={() => navigate(`/admin/sites/${g.siteId}/periods/${g.firstPeriod}`)}>
              <strong>{g.siteName}</strong> — {g.open} open {g.positions.map(slotLabel).join(' / ')} slot-day{g.open === 1 ? '' : 's'} across {g.weeks} week{g.weeks === 1 ? '' : 's'} from {dayLabel(g.firstWeek).replace(/^\w+, /, '')}
            </Row>
          )} />
          <More label="timesheet packets" items={a.awaitingApproval} render={(t) => (
            <Row key={`${t.siteId}|${t.periodStart}`} tone="info" onClick={() => navigate(`/admin/timesheets?siteId=${t.siteId}&periodStart=${t.periodStart}`)}>
              <strong>{t.siteName}</strong> timesheets, tour of {dayLabel(t.periodStart).replace(/^\w+, /, '')} — {t.approved} of {t.sheets} approved{t.questions ? `, ${t.questions} question${t.questions === 1 ? '' : 's'} from physicians` : ''}
            </Row>
          )} />
          <More label="licences running out" items={a.licenses ?? []} render={(l) => (
            <Row key={`${l.physician.abbreviation}|${l.state}`} tone={l.warning === 'expired' ? 'err' : 'warn'} onClick={() => navigate(`/admin/roster/${encodeURIComponent(l.physician.abbreviation)}`)}>
              <strong>{l.physician.firstName} {l.physician.lastName}</strong> — {l.state} licence {l.warning === 'expired' ? 'expired' : 'expires'} {dayLabel(l.expiresOn)}
            </Row>
          )} />
          <More label="credentialing questions" items={groupCred(a.credentialing)} render={(g) => (
            <Row key={`${g.abbreviation}|${g.siteId}|${g.issue}`} tone="warn" onClick={() => navigate(`/admin/sites/${g.siteId}/periods/${g.firstWeek}`)}>
              <strong>{g.name}</strong> is scheduled at {g.siteName} ({g.weeks} week{g.weeks === 1 ? '' : 's'} from {dayLabel(g.firstWeek).replace(/^\w+, /, '')}) — {g.issue}
            </Row>
          )} />
          {a.onboarding.map((p) => (
            <Row key={p.abbreviation} tone="info">
              <strong>{p.firstName} {p.lastName}</strong> is onboarding{p.startDate ? `, starts ${dayLabel(p.startDate)}` : ' — no start date yet'}{p.scheduled ? ' · already on the schedule' : ''}
            </Row>
          ))}
        </section>

        <section className="card">
          <h2>Recent changes</h2>
          {data.recent.length === 0 && <p className="muted">No changes recorded yet.</p>}
          <ul className="recent">
            {data.recent.map((r, i) => (
              <li key={i}>
                <button className="linklike" type="button" onClick={() => navigate(r.siteId && r.periodStart ? `/admin/sites/${r.siteId}/periods/${r.periodStart}` : `/admin/requests?quarter=${r.quarter ?? ''}`)}>
                  <span className={`pill tiny${r.kind === 'availability' ? ' warn' : ''}`}>{r.kind}</span> {r.what}
                </button>
                <div className="muted small">{new Date(r.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {r.who}{r.why ? ` — ${r.why}` : ''}</div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  );
}

/**
 * One hospital's card. Read-only until a name is in "Edits recorded as" — the same gate the grid
 * uses, so the two screens agree about when the schedule may be touched (D93).
 */
function SiteCard({
  c, focusDay, quarter, opening, onEdit, onAdd,
}: {
  c: DashboardCard;
  focusDay: string;
  quarter: string;
  /** The id of the row whose week is being fetched, so that one row can say it is working. */
  opening: string | null;
  onEdit?: (row: OnDuty) => void;
  onAdd?: (slot: string) => void;
}) {
  const open = c.period ? () => navigate(`/admin/sites/${c.siteId}/periods/${c.period!.periodStart}`) : () => navigate(`/admin/grid?quarter=${quarter}`);
  const tone = !c.period ? 'none' : c.flags.errors ? 'err' : c.openSlotDays ? 'open' : 'ok';
  return (
    <article className={`sitecard tone-${tone}`}>
      <header>
        <span className={`letter site-${c.siteId}`}>{c.letter}</span>
        <button className="linklike name" type="button" onClick={open}>{c.name}</button>
        {c.period && <span className={`pill status-${c.period.status}`}>{c.period.status}</span>}
      </header>
      {!c.period ? (
        <p className="muted small">No schedule for this week.</p>
      ) : (
        <>
          <div className="stats">
            <span title="physicians with a shift this week">{c.physicians} on</span>
            <span className={c.openSlotDays ? 'err' : 'ok'} title="expected slot-days with nobody">{c.openSlotDays ? `${c.openSlotDays} open` : 'covered'}</span>
            {c.flags.errors > 0 && <span className="err">{c.flags.errors} error{c.flags.errors === 1 ? '' : 's'}</span>}
            {c.flags.warnings > 0 && <span className="warn">{c.flags.warnings} warning{c.flags.warnings === 1 ? '' : 's'}</span>}
          </div>
          <ul className="onduty">
            {c.onDuty.map((s) => (
              <li key={s.id}>
                {onEdit ? (
                  <button
                    type="button"
                    className="linklike dutyrow"
                    disabled={opening === s.id}
                    onClick={() => onEdit(s)}
                    title={`${s.physician.firstName} ${s.physician.lastName} · ${slotLabel(s.positionSlot)} · click to change`}
                  >
                    <span className="slot">{slotLabel(s.positionSlot)}</span>
                    <span>{s.physician.lastName}</span>
                    <span className="hours">{opening === s.id ? 'opening…' : s.start && s.end ? `${hourLabel(s.start)}–${hourLabel(s.end)}` : 'on call'}</span>
                  </button>
                ) : (
                  <>
                    <span className="slot">{slotLabel(s.positionSlot)}</span>
                    <span>{s.physician.lastName}</span>
                    <span className="hours">{s.start && s.end ? `${hourLabel(s.start)}–${hourLabel(s.end)}` : 'on call'}</span>
                  </>
                )}
              </li>
            ))}
            {c.openToday.map((slot) => (
              <li key={`open-${slot}`} className="err">
                {onAdd ? (
                  <button type="button" className="linklike dutyrow" onClick={() => onAdd(slot)} title={`Fill ${slotLabel(slot)} on ${dayLabel(focusDay)}`}>
                    <span className="slot">{slotLabel(slot)}</span>
                    <span>— nobody —</span>
                    <span className="hours">fill</span>
                  </button>
                ) : (
                  <>
                    <span className="slot">{slotLabel(slot)}</span>
                    <span>— nobody —</span>
                  </>
                )}
              </li>
            ))}
            {c.onDuty.length === 0 && c.openToday.length === 0 && <li className="muted">Nothing on {dayLabel(focusDay).replace(/^\w+, /, '')}.</li>}
          </ul>
          <footer>
            <button className="linklike small" type="button" onClick={() => navigate(`/admin/hospitals/${c.siteId}?quarter=${quarter}`)}>hospital grid</button>
            <button className="linklike small" type="button" onClick={() => navigate(`/admin/timesheets?siteId=${c.siteId}&periodStart=${c.period!.periodStart}`)}>timesheets</button>
          </footer>
        </>
      )}
    </article>
  );
}

/** The first few of a long list, with the rest a click away. */
function More<T>({ label, items, render, limit = 4 }: { label: string; items: T[]; render: (item: T) => React.ReactNode; limit?: number }) {
  const [all, setAll] = useState(false);
  if (items.length === 0) return null;
  const shown = all ? items : items.slice(0, limit);
  return (
    <>
      {shown.map(render)}
      {items.length > limit && (
        <button className="linklike small more" type="button" onClick={() => setAll((v) => !v)}>
          {all ? `show fewer ${label}` : `show all ${items.length} ${label}`}
        </button>
      )}
    </>
  );
}

function Row({ children, tone, onClick }: { children: React.ReactNode; tone: 'ok' | 'warn' | 'err' | 'info'; onClick?: () => void }) {
  const inner = <span className={`dot ${tone}`} />;
  return onClick ? (
    <button type="button" className="attn" onClick={onClick}>{inner}<span>{children}</span></button>
  ) : (
    <div className="attn">{inner}<span>{children}</span></div>
  );
}

/** Open slots per site, summarised across weeks so seven nights at one site read as one line. */
function groupOpen(rows: DashboardData['attention']['openSlots']) {
  const m = new Map<string, { siteId: string; siteName: string; positions: string[]; open: number; weeks: number; firstWeek: string; firstPeriod: string }>();
  for (const r of rows) {
    const key = `${r.siteId}|${r.positions.join(',')}`;
    const g = m.get(key) ?? { siteId: r.siteId, siteName: r.siteName, positions: r.positions, open: 0, weeks: 0, firstWeek: r.week, firstPeriod: r.periodStart };
    g.open += r.open;
    g.weeks += 1;
    if (r.week < g.firstWeek) { g.firstWeek = r.week; g.firstPeriod = r.periodStart; }
    m.set(key, g);
  }
  return [...m.values()].sort((a, b) => a.firstWeek.localeCompare(b.firstWeek) || b.open - a.open);
}

function groupCred(rows: DashboardData['attention']['credentialing']) {
  const m = new Map<string, { abbreviation: string; name: string; siteId: string; siteName: string; issue: string; weeks: number; firstWeek: string }>();
  for (const r of rows) {
    const key = `${r.physician.abbreviation}|${r.siteId}|${r.issue}`;
    const g = m.get(key) ?? { abbreviation: r.physician.abbreviation, name: `${r.physician.firstName} ${r.physician.lastName}`, siteId: r.siteId, siteName: r.siteName, issue: r.issue, weeks: 0, firstWeek: r.week };
    g.weeks += 1;
    if (r.week < g.firstWeek) g.firstWeek = r.week;
    m.set(key, g);
  }
  return [...m.values()].sort((a, b) => a.firstWeek.localeCompare(b.firstWeek) || a.name.localeCompare(b.name));
}

function quarterLabel(q: string): string {
  const [y, n] = q.split('-Q');
  return `Q${n} ${y}`;
}
